import { BaseService } from './base';
import { ApiRequest } from '../lib/request';
import type { ApiResponse, ApiListResponse } from '../lib/response';
import {
  Order,
  OrderListMonth,
  OrderTerminalPayment,
  OrderTipType,
} from '../models/order';
import { OrderItem } from '../models/orderItem';

export class OrderService extends BaseService<Order> {
  override model = Order;

  override modelName = 'order';
  override modelLabel = 'Orders';

  // Static API Methods

  async filter({
    page = 1,
    limit = 20,
    filters = {},
  }: {
    page?: number;
    limit?: number;
    filters?: Record<string, any>;
  } = {}): Promise<ApiListResponse<Order>> {
    const query: Record<string, string> = {
      page: page.toString(),
      limit: limit.toString(),
    };

    for (const filterType in filters) {
      const filterParams = filters[filterType];

      for (const key in filterParams) {
        const value = filterParams[key];

        if (Array.isArray(value)) {
          value.forEach((v, i) => {
            query[`${key}[${i}]`] = v.toString();
          });
        } else if (value !== null && value !== undefined) {
          query[key] = value.toString();
        }
      }
    }

    const request = new ApiRequest({
      method: 'GET',
      url: this.modelBaseUrl,
      query,
    });

    return await this.requestList<Order>(request, Order);
  }

  async create({
    customerId,
    notes,
    items = [],
    date,
  }: {
    customerId: string;
    notes: string;
    items?: { type: string; id: string }[];
    date?: number;
  }): Promise<ApiResponse<Order>> {
    const request = new ApiRequest({
      method: 'POST',
      url: this.modelBaseUrl,
      data: {
        idCustomer: customerId,
        date,
        notes,
        items,
      },
    });

    return await this.requestItem<Order>(request, Order);
  }

  async recentCharges(): Promise<ApiListResponse<OrderItem>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/recentCharges`,
    });

    return await this.requestList<OrderItem>(request, OrderItem);
  }

  async listMonth(
    year: number,
    month: number
  ): Promise<ApiResponse<OrderListMonth>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/month`,
      query: { year: year.toString(), month: month.toString() },
    });

    return await this.requestItem<OrderListMonth>(request, OrderListMonth);
  }

  async listIDs(
    ids: string[],
    page: number = 1
  ): Promise<ApiListResponse<Order>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/ids`,
      data: { ids },
      query: { page: page.toString() },
    });

    return await this.requestList<Order>(request, Order);
  }

  // Instance-style API Methods (take order ID)

  async email(orderId: string): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${orderId}/email`,
    });

    return await this.request(request);
  }

  async open(orderId: string): Promise<ApiResponse<Order>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${orderId}/open`,
    });

    return await this.requestItem<Order>(request, Order);
  }

  async close(
    orderId: string,
    { sendEmail = false }: { sendEmail?: boolean } = {}
  ): Promise<ApiResponse<Order>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${orderId}/close`,
      query: { email: sendEmail ? '1' : '0' },
    });

    return await this.requestItem<Order>(request, Order);
  }

  async addProduct(
    orderId: string,
    { productId, quantity, employeeId }: { productId: string; quantity: number; employeeId?: string }
  ): Promise<ApiResponse<Order>> {
    const query: Record<string, string> = {
      product: productId,
      quantity: quantity.toString(),
    };
    if (employeeId) query.employee = employeeId;

    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${orderId}/addProduct`,
      query,
    });

    return await this.requestItem<Order>(request, Order);
  }

  async addCustomerCredit(
    orderId: string,
    { price, employeeId }: { price: number; employeeId?: string }
  ): Promise<ApiResponse<Order>> {
    const query: Record<string, string> = {
      price: price.toString(),
    };
    if (employeeId) query.employee = employeeId;

    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${orderId}/addCustomerCredit`,
      query,
    });

    return await this.requestItem<Order>(request, Order);
  }

  async addProducts(
    orderId: string,
    {
      quantities,
      employeeId,
    }: {
      quantities: { productId: string; quantity: number }[];
      employeeId?: string;
    }
  ): Promise<ApiResponse<Order>> {
    const query: Record<string, string> = {};
    if (employeeId) query.employee = employeeId;

    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${orderId}/addProducts`,
      query,
      data: {
        quantities: quantities.map(({ productId, quantity }) => ({
          product: productId,
          quantity,
        })),
      },
    });

    return await this.requestItem<Order>(request, Order);
  }

  async addProductWithBarcode(
    orderId: string,
    barcode: string
  ): Promise<ApiResponse<Order>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${orderId}/addProductwithBarcode`,
      data: { barcode },
    });

    return await this.requestItem<Order>(request, Order);
  }

  async addService(
    orderId: string,
    { serviceId, employeeId }: { serviceId: string; employeeId?: string }
  ): Promise<ApiResponse<Order>> {
    const query: Record<string, string> = {
      service: serviceId,
    };
    if (employeeId) query.employee = employeeId;

    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${orderId}/addService`,
      query,
    });

    return await this.requestItem<Order>(request, Order);
  }

  async addReservation(
    orderId: string,
    reservationId: string
  ): Promise<ApiResponse<Order>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${orderId}/addReservation`,
      query: { reservation: reservationId },
    });

    return await this.requestItem<Order>(request, Order);
  }

  async addReservations(
    orderId: string,
    {
      reservationIds = [],
      reservationCustomerIds = [],
    }: {
      reservationIds?: string[];
      reservationCustomerIds?: string[];
    }
  ): Promise<ApiResponse<Order>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${orderId}/addReservations`,
      data: {
        reservations: reservationIds,
        reservationCustomers: reservationCustomerIds,
      },
    });

    return await this.requestItem<Order>(request, Order);
  }

  async addReservationEmployee(
    orderId: string,
    { reservationId, employeeId }: { reservationId: string; employeeId?: string }
  ): Promise<ApiResponse<Order>> {
    const query: Record<string, string> = {
      reservation: reservationId,
    };
    if (employeeId) query.employee = employeeId;

    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${orderId}/addReservation`,
      query,
    });

    return await this.requestItem<Order>(request, Order);
  }

  async addReservationCustomer(
    orderId: string,
    reservationCustomerId: string
  ): Promise<ApiResponse<Order>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${orderId}/addReservationCustomer`,
      query: { reservationCustomer: reservationCustomerId },
    });

    return await this.requestItem<Order>(request, Order);
  }

  async addReservationCustomers(
    orderId: string,
    reservationCustomerIds: string[]
  ): Promise<ApiResponse<Order>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${orderId}/addReservationCustomers`,
      data: {
        reservationCustomers: reservationCustomerIds,
      },
    });

    return await this.requestItem<Order>(request, Order);
  }

  async addGiftCard(
    orderId: string,
    giftCardId: string
  ): Promise<ApiResponse<Order>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${orderId}/addGiftCard`,
      query: { giftCard: giftCardId },
    });

    return await this.requestItem<Order>(request, Order);
  }

  async addGiftCards(
    orderId: string,
    giftCardIds: string[]
  ): Promise<ApiResponse<Order>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${orderId}/addGiftCards`,
      data: { giftCards: giftCardIds },
    });

    return await this.requestItem<Order>(request, Order);
  }

  async addCustomerPasses(
    orderId: string,
    customerPassIds: string[]
  ): Promise<ApiResponse<Order>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${orderId}/addCustomerPasses`,
      data: { customerPasses: customerPassIds },
    });

    return await this.requestItem<Order>(request, Order);
  }

  async addCustomerCourses(orderId: string, customerCourseIds: string[]): Promise<ApiResponse<Order>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${orderId}/addCustomerCourses`,
      data: { customerCourses: customerCourseIds },
    });

    return await this.requestItem<Order>(request, Order);
  }

  async addCharge(
    orderId: string,
    charge: Record<string, any>
  ): Promise<ApiResponse<Order>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${orderId}/addCharge`,
      data: charge,
    });

    return await this.requestItem<Order>(request, Order);
  }

  async addDiscount(
    orderId: string,
    discount: Record<string, any>
  ): Promise<ApiResponse<Order>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${orderId}/addDiscount`,
      data: discount,
    });

    return await this.requestItem<Order>(request, Order);
  }

  async addPayment(
    orderId: string,
    payment: Record<string, any>
  ): Promise<ApiResponse<Order>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${orderId}/addPayment`,
      data: payment,
    });

    return await this.requestItem<Order>(request, Order);
  }

  async addPaymentSavedCreditCard(
    orderId: string,
    {
      amount,
      creditCardId,
      idempotencyKey,
    }: {
      amount: number;
      creditCardId: string;
      idempotencyKey: string;
    }
  ): Promise<ApiResponse<Order>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${orderId}/addPaymentSavedCreditCard`,
      query: {
        amount: amount.toString(),
        idCustomerCreditCard: creditCardId,
        idempotency_key: idempotencyKey,
      },
    });

    return await this.requestItem<Order>(request, Order);
  }

  async addPaymentNewCreditCard(
    orderId: string,
    {
      amount,
      processor,
      params,
      saveCard,
    }: {
      amount: number;
      processor: string;
      params: Record<string, any>;
      saveCard: boolean;
    }
  ): Promise<ApiResponse<Order>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${orderId}/addPaymentSavedCreditCard`,
      data: { amount, processor, saveCard, ...params },
    });

    return await this.requestItem<Order>(request, Order);
  }

  async addPaymentTerminal(
    orderId: string,
    {
      guid,
      amount,
      params = {},
    }: {
      guid: string;
      amount: number;
      params?: Record<string, any>;
    }
  ): Promise<ApiResponse<OrderTerminalPayment>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${orderId}/addPaymentTerminal`,
      data: { guid, amount, params },
    });

    return await this.requestItem<OrderTerminalPayment>(request, OrderTerminalPayment);
  }

  async checkPaymentTerminal(
    orderId: string,
    { guid, paymentId }: { guid: string; paymentId: string }
  ): Promise<ApiResponse<OrderTerminalPayment>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${orderId}/checkPaymentTerminal`,
      data: { guid, payment_id: paymentId },
    });

    return await this.requestItem<OrderTerminalPayment>(request, OrderTerminalPayment);
  }

  async cancelPaymentTerminal(
    orderId: string,
    { guid, paymentId }: { guid: string; paymentId: string }
  ): Promise<ApiResponse<OrderTerminalPayment>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${orderId}/cancelPaymentTerminal`,
      data: { guid, payment_id: paymentId },
    });

    return await this.requestItem<OrderTerminalPayment>(request, OrderTerminalPayment);
  }

  async saveItem(
    orderId: string,
    item: Record<string, any>,
    { removeVoucher = false }: { removeVoucher?: boolean } = {}
  ): Promise<ApiResponse<Order>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${orderId}/saveItem`,
      data: item,
      query: { removeVoucher: removeVoucher ? '1' : '0' },
    });

    return await this.requestItem<Order>(request, Order);
  }

  async deleteItem(
    orderId: string,
    itemId: string
  ): Promise<ApiResponse<Order>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${orderId}/deleteItem`,
      query: { item: itemId },
    });

    return await this.requestItem<Order>(request, Order);
  }

  async refundPayment(
    orderId: string,
    {
      orderItemId,
      amount,
      processorRefund,
      customerPayment,
    }: {
      orderItemId: string;
      amount: number;
      processorRefund: boolean;
      customerPayment?: Record<string, any> | null;
    }
  ): Promise<ApiResponse<Order>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/refundPayment`,
      data: {
        order: orderId,
        orderItem: orderItemId,
        amount,
        processorRefund,
        customerPayment,
      },
    });

    return await this.requestItem<Order>(request, Order);
  }

  async requestPayment(
    orderId: string,
    { amount, details }: { amount: number; details: any }
  ): Promise<ApiResponse<Order>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/requestPayment`,
      data: {
        order: orderId,
        amount,
        details,
      },
    });

    return await this.requestItem<Order>(request, Order);
  }

  async setTipSingle(
    orderId: string,
    amount: number
  ): Promise<ApiResponse<Order>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${orderId}/setTip`,
      data: {
        tipType: OrderTipType.single,
        amount,
      },
    });

    return await this.requestItem<Order>(request, Order);
  }

  async setTipEmployee(
    orderId: string,
    employeeTips: { employeeId: string; amount: number }[]
  ): Promise<ApiResponse<Order>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${orderId}/setTip`,
      data: {
        tipType: OrderTipType.perEmployee,
        employeeTips: employeeTips.map(({ employeeId, amount }) => ({
          employee: employeeId,
          amount,
        })),
      },
    });

    return await this.requestItem<Order>(request, Order);
  }
}
